import { ParameterData } from "../../../types/device";

interface LiveTableProps {
  parameters: ParameterData[];
  isEditMode?: boolean;
  onEditParameter?: (parameter: ParameterData) => void;
  onDeleteParameter?: (parameterId?: string) => void;
  isLiveDataConnected?: boolean;
}

export function LiveTable({
  parameters,
  isEditMode = false,
  onEditParameter,
  onDeleteParameter,
  isLiveDataConnected = false,
}: LiveTableProps) {
  const calculateProgress = (parameter: ParameterData) => {
    // STRING values have no numeric range, so there is no progress to show.
    if (typeof parameter.value === "string") return 0;
    const value = (parameter.value || 0) * (parameter.calibration || 1);
    if (
      parameter.minValue !== undefined &&
      parameter.maxValue !== undefined &&
      parameter.minValue !== null &&
      parameter.maxValue !== null &&
      value !== undefined
    ) {
      return Math.min(
        100,
        Math.max(
          0,
          ((value - parameter.minValue) / (parameter.maxValue - parameter.minValue)) * 100,
        ),
      );
    }
    return 0;
  };

  return (
    <div className="bg-transparent rounded-xl border border-border shadow-sm overflow-hidden">
      {/* Table Header */}
      <div className="bg-gradient-to-r from-slate-50 to-gray-50 border-b border-border px-6 py-4">
        <div
          className={`grid gap-4 items-center text-xs font-semibold text-gray-500 uppercase tracking-wider ${
            isEditMode ? "grid-cols-12" : "grid-cols-10"
          }`}
        >
          <div className="col-span-4">Parameter</div>
          <div className="col-span-2 text-right">Value</div>
          <div className="col-span-2 text-center">Status</div>

          <div className="col-span-2 text-center">Range</div>
          {isEditMode && <div className="col-span-2 text-center">Actions</div>}
        </div>
      </div>

      {/* Table Body */}
      <div className="divide-y divide-gray-100">
        {parameters.map((parameter, index) => {
          // STRING params render their raw text (e.g. "NA", "OPEN"); numeric
          // params apply calibration and locale formatting below.
          const isStringValue = typeof parameter.value === "string";
          const value = isStringValue
            ? (parameter.value as string)
            : ((parameter.value as number) || 0) * (parameter.calibration || 1);
          return (
            <div
              key={parameter.id}
              className="px-6 py-4 hover:bg-slate-50/50 transition-all duration-200 group"
              style={{ animationDelay: `${index * 25}ms` }}
            >
              <div
                className={`grid gap-4 items-center ${
                  isEditMode ? "grid-cols-12" : "grid-cols-10"
                }`}
              >
                {/* Parameter Name & Icon */}
                <div className="col-span-4 flex items-center gap-3">
                  <div
                    className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-200 ${
                      value !== undefined ? "bg-blue-50 group-hover:bg-blue-100" : "bg-gray-50"
                    }`}
                  >
                    <div
                      className={`w-5 h-5 flex items-center justify-center ${
                        value !== undefined ? "text-blue-600" : "text-gray-400"
                      }`}
                    >
                      {parameter.iconElement}
                    </div>
                  </div>
                  <div>
                    <div className="font-semibold text-sm text-gray-900">{parameter.name}</div>
                    <div className="text-xs text-gray-500 flex items-center gap-1 flex-wrap">
                      <span>{parameter.unit || "—"}</span>
                    </div>
                  </div>
                </div>

                {/* Value */}
                <div className="col-span-2 text-right">
                  <div
                    className={`font-mono text-lg font-bold transition-all duration-300 ${
                      value !== undefined ? "text-gray-900" : "text-gray-400"
                    }`}
                  >
                    {isStringValue
                      ? (value as string)
                      : (value as number).toLocaleString("en-US", {
                          minimumFractionDigits: (value as number) % 1 === 0 ? 0 : 2,
                          maximumFractionDigits: 2,
                        })}
                  </div>
                  {/* {value !== undefined && (
                    <div className="text-xs text-green-600 font-medium flex items-center justify-end gap-1">
                      <div className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></div>
                      Real-time
                    </div>
                  )} */}
                </div>

                {/* Status */}
                <div className="col-span-2 text-center">
                  {parameter.minValue !== undefined &&
                  parameter.maxValue !== undefined &&
                  parameter.minValue !== null &&
                  parameter.maxValue !== null ? (
                    <div
                      className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 ${
                        parameter.status === "normal"
                          ? "bg-green-50 text-green-700 border border-green-200"
                          : parameter.status === "warning"
                            ? "bg-yellow-50 text-yellow-700 border border-yellow-200"
                            : "bg-red-50 text-red-700 border border-red-200"
                      }`}
                    >
                      <div
                        className={`w-2 h-2 rounded-full mr-2 animate-pulse ${
                          parameter.status === "normal"
                            ? "bg-green-500"
                            : parameter.status === "warning"
                              ? "bg-yellow-500"
                              : "bg-red-500"
                        }`}
                      ></div>
                      {parameter.status === "normal"
                        ? "Normal"
                        : parameter.status === "warning"
                          ? "Warning"
                          : "Critical"}
                    </div>
                  ) : (
                    <div className="text-center text-xs text-gray-400 font-medium">-</div>
                  )}
                </div>

                {/* Progress Bar */}
                <div className="col-span-2">
                  {parameter.minValue !== undefined &&
                  parameter.maxValue !== undefined &&
                  parameter.minValue !== null &&
                  parameter.maxValue !== null ? (
                    <div className="space-y-2">
                      <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
                        <div
                          className={`h-3 rounded-full transition-all duration-700 ease-out ${
                            parameter.status === "normal"
                              ? "bg-gradient-to-r from-green-400 to-green-500"
                              : parameter.status === "warning"
                                ? "bg-gradient-to-r from-yellow-400 to-yellow-500"
                                : "bg-gradient-to-r from-red-400 to-red-500"
                          }`}
                          style={{ width: `${calculateProgress(parameter)}%` }}
                        />
                      </div>
                      <div className="flex justify-between text-xs text-gray-500 font-medium">
                        <span className="bg-gray-50 px-2 py-0.5 rounded">{parameter.minValue}</span>
                        <span className="bg-gray-50 px-2 py-0.5 rounded">{parameter.maxValue}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center text-xs text-gray-400 font-medium">-</div>
                  )}
                </div>

                {/* Actions */}
                {isEditMode && (
                  <div className="col-span-2 flex justify-center space-x-2">
                    <button
                      onClick={() => onEditParameter?.(parameter)}
                      className="p-2 rounded-lg hover:bg-blue-50 text-blue-600 hover:text-blue-700 transition-all duration-200 border border-blue-200 hover:border-blue-300 text-xs font-medium"
                      title="Edit parameter"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => onDeleteParameter?.(parameter.id)}
                      className="p-2 rounded-lg hover:bg-red-50 text-red-600 hover:text-red-700 transition-all duration-200 border border-red-200 hover:border-red-300 text-xs font-medium"
                      title="Delete parameter"
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Table Footer */}
      <div className="bg-gray-50/50 border-t border-gray-100 px-6 py-3">
        <div className="flex items-center justify-between text-sm text-gray-900">
          <span className="font-medium">
            Showing {parameters.length} Parameter{parameters.length !== 1 ? "s" : ""}
          </span>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span className="text-xs">Normal</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
              <span className="text-xs">Warning</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 bg-red-500 rounded-full"></div>
              <span className="text-xs">Critical</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
              <span className="text-xs">Offline</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
